import axios, { AxiosInstance, AxiosError, isAxiosError } from 'axios'
import { ExifData } from '../types/image'

export const API_GATEWAY_URL = import.meta.env.VITE_API_URL || 'https://oy0j07x4ll.execute-api.us-east-2.amazonaws.com/prod'
export const ADMIN_API_URL = import.meta.env.VITE_ADMIN_API_URL || API_GATEWAY_URL

// Public API Client - Uses CloudFront (cached, no auth)
export const publicApiClient: AxiosInstance = axios.create({
  baseURL: `${API_GATEWAY_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Admin API Client - Uses direct API Gateway (auth required)
export const adminApiClient: AxiosInstance = axios.create({
  baseURL: `${ADMIN_API_URL}/admin/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Add auth interceptor to admin client
adminApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Add token refresh interceptor to admin client
adminApiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && originalRequest) {
      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          // Use public client for refresh (auth endpoints are public)
          const response = await axios.post(`${API_GATEWAY_URL}/api/auth/refresh`, {
            refreshToken,
          })
          
          const { accessToken, refreshToken: newRefreshToken } = response.data.data
          localStorage.setItem('accessToken', accessToken)
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken)
          }
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return adminApiClient(originalRequest)
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

const SHARE_TOKEN_KEY = (albumId: string) => `shareToken:${albumId}`;

export function getShareToken(albumId: string): string | null {
  return localStorage.getItem(SHARE_TOKEN_KEY(albumId));
}

export function setShareToken(albumId: string, token: string): void {
  localStorage.setItem(SHARE_TOKEN_KEY(albumId), token);
}

export function clearShareToken(albumId: string): void {
  localStorage.removeItem(SHARE_TOKEN_KEY(albumId));
}

// Legacy client for backward compatibility (uses public API)
export const apiClient = publicApiClient

export const handleApiError = (error: unknown): string => {
  if (isAxiosError(error)) {
    const data = error.response?.data as { error?: { message: string } } | undefined
    if (data?.error?.message) {
      return data.error.message
    }
    return error.message || 'An unexpected error occurred'
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'An unexpected error occurred'
}

export const refreshImageExif = async (imageId: string): Promise<{ exifData: ExifData; dateCaptured: string }> => {
  const { data } = await apiClient.post(`/images/${imageId}/refresh-exif`)
  return data.data
}
